import { Link } from 'react-router-dom';
import { propFirmLogos } from '../data/propFirmLogos';
import { StarRating } from './StarRating';
import { PromoCTAButton } from './PromoCTAButton';
import type { PropFirm } from '../types';

// Utility for countdown
function getTimeLeft(validUntil: string): string | null {
  const now = new Date();
  const end = new Date(validUntil);
  const diff = end.getTime() - now.getTime();
  if (diff <= 0) return null;

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);

  if (days > 0) return `${days} day${days > 1 ? 's' : ''} left`;
  if (hours > 0) return `${hours}h ${minutes}m left`;
  return `${minutes} minutes left`;
}

interface ComparisonTableProps {
  firms: PropFirm[];
  variant?: 'default' | 'compare';
}

export function ComparisonTable({ firms, variant = 'default' }: ComparisonTableProps) {
  return (
    <div className="w-full">
      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full border border-gray-800 rounded-xl overflow-hidden bg-gray-950">
          <thead className="bg-gray-900 text-yellow-400 text-sm">
            <tr>
              <th className="px-4 py-3 text-left">Firm</th>
              {variant === 'compare' && <th className="px-4 py-3 text-left">Challenge Type</th>}
              {variant === 'compare' && <th className="px-4 py-3 text-left">Platform</th>}
              <th className="px-4 py-3 text-left">Max Funding</th>
              <th className="px-4 py-3 text-left">Profit Split</th>
              {variant === 'compare' && <th className="px-4 py-3 text-left">Payout</th>}
              <th className="px-4 py-3 text-left">Rating</th>
              <th className="px-4 py-3 text-center">Promo</th>
            </tr>
          </thead>
          <tbody className="text-yellow-300 text-sm divide-y divide-gray-800">
            {firms.map((firm) => {
              const logo = propFirmLogos[firm.id]?.logo;
              const discount = firm.evaluation?.discount;
              const countdown = discount?.validUntil ? getTimeLeft(discount.validUntil) : null;

              return (
                <tr key={firm.id} className="hover:bg-gray-800 transition">
                  <td className="px-4 py-3 flex items-center gap-3">
                    <div className="w-8 h-8 rounded bg-black flex items-center justify-center border border-gray-700">
                      <img src={logo} alt={firm.name} className="w-6 h-6 object-contain" />
                    </div>
                    <Link
                      to={`/reviews/${firm.id}`}
                      className="font-semibold text-yellow-200 hover:text-white"
                    >
                      {firm.name}
                    </Link>
                  </td>

                  {variant === 'compare' && (
                    <td className="px-4 py-3">
                      {firm.evaluation?.types?.length > 0 ? firm.evaluation.types.join(', ') : '-'}
                    </td>
                  )}

                  {variant === 'compare' && (
                    <td className="px-4 py-3">
                      {firm.features?.platforms?.length > 0 ? firm.features.platforms.join(', ') : '-'}
                    </td>
                  )}

                  <td className="px-4 py-3">{firm.features?.maxFunding || '-'}</td>
                  <td className="px-4 py-3">{firm.features?.profitSplit || '-'}</td>

                  {variant === 'compare' && (
                    <td className="px-4 py-3">{firm.features?.payout || '-'}</td>
                  )}

                  <td className="px-4 py-3">
                    <StarRating rating={firm.rating} />
                    <span className="ml-1 text-sm text-yellow-300">
                      ({firm.reviewCount?.toLocaleString() || 0})
                    </span>
                  </td>

                  <td className="px-4 py-3 text-center w-[200px]">
                    <PromoCTAButton
                      firmId={firm.id}
                      code={discount?.code}
                      percent={discount?.percent}
                      validUntilRemark={discount?.validUntilRemark}
                      firstPurchaseOnly={discount?.firstPurchaseOnly}
                    />
                    {countdown && (
                      <div className="text-xs text-yellow-400 mt-1">⏳ {countdown}</div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile Grid View */}
      <div className="md:hidden space-y-6 mt-8">
        {firms.map((firm) => {
          const logo = propFirmLogos[firm.id]?.logo;
          const discount = firm.evaluation?.discount;
          const countdown = discount?.validUntil ? getTimeLeft(discount.validUntil) : null;

          return (
            <div
              key={firm.id}
              className="bg-gray-900 border border-gray-800 text-yellow-100 rounded-xl shadow-md sm:p-5 p-4 space-y-4 hover:shadow-lg transition-shadow hover:scale-[1.01]"
            >
              <div className="flex items-center gap-4">
                <div className="bg-black p-2 rounded">
                  <img src={logo} alt={firm.name} className="w-10 h-10 object-contain" />
                </div>
                <div>
                  <Link
                    to={`/reviews/${firm.id}`}
                    className="text-lg font-bold text-yellow-300 hover:underline"
                  >
                    {firm.name}
                  </Link>
                  {countdown && (
                    <div className="text-xs text-yellow-400 mt-1">⏳ {countdown}</div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm text-yellow-300 mt-2">
                <div>
                  <div className="text-xs uppercase text-yellow-500 mb-1">Max Funding</div>
                  <div className="font-semibold">{firm.features?.maxFunding || '-'}</div>
                </div>
                <div>
                  <div className="text-xs uppercase text-yellow-500 mb-1">Profit Split</div>
                  <div className="font-semibold">{firm.features?.profitSplit || '-'}</div>
                </div>
              </div>

              <div className="flex justify-center items-center gap-2 mt-1 text-yellow-400 text-sm">
                <StarRating rating={firm.rating} />
                <span>({firm.reviewCount?.toLocaleString() || 0} reviews)</span>
              </div>

              <div className="pt-2">
                <PromoCTAButton
                  firmId={firm.id}
                  code={discount?.code}
                  percent={discount?.percent}
                  validUntilRemark={discount?.validUntilRemark}
                  firstPurchaseOnly={discount?.firstPurchaseOnly}
                  className="w-full"
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
